import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { Api, HTTPMethods, Query } from './types';
import {
  AlgoInput,
  AlgoOutput,
  AnalyteReport,
  HormoneReport,
  Quiz,
  QuizResponse,
  UserDnaCategory,
  UserGetOut,
  UserNutrientSummary,
} from '../../types';
import { fetchSanitySettings, sendRequest } from './utils';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 10, // 10 seconds
    },
  },
});

export const Queries: Record<Api, Query> = {
  AlgoInputs: {
    key: ['algoInputs'],
    endpoint: {
      path: '/admin/algorithm_inputs/:email',
      method: HTTPMethods.GET,
      pathParams: ['email'],
    },
  },
  RunAlgo: {
    key: ['runAlgo'],
    endpoint: {
      path: '/admin/run_algorithm',
      method: HTTPMethods.POST,
    },
  },
  Analytes: {
    key: ['analytes'],
    endpoint: {
      path: '/analytes',
      method: HTTPMethods.GET,
    },
  },
  DnaCategoryResults: {
    key: ['dna'],
    endpoint: {
      path: '/dna/category/results',
      method: HTTPMethods.GET,
    },
  },
  Hormones: {
    key: ['hormones'],
    endpoint: {
      path: '/hormones',
      method: HTTPMethods.GET,
    },
  },
  Me: {
    key: ['me'],
    endpoint: {
      path: '/users/me',
      method: HTTPMethods.GET,
    },
  },
  Nutrients: {
    key: ['nutrients'],
    endpoint: {
      path: '/users/nutrients',
      method: HTTPMethods.GET,
    },
  },
  Quiz: {
    key: ['quiz'],
    endpoint: {
      path: '/quizzes/:code',
      method: HTTPMethods.GET,
      pathParams: ['code'],
    },
  },
  SubmitQuizResponse: {
    key: ['submitQuizResponse'],
    endpoint: {
      path: '/quizzes/response',
      method: HTTPMethods.POST,
    },
  },
  UserRequest: {
    key: ['userRequest'],
    endpoint: {
      path: '/users/:id/request',
      method: HTTPMethods.POST,
      pathParams: ['id'],
    },
  },
};

export const useAlgoInputs = () =>
  useMutation({
    mutationKey: Queries.AlgoInputs.key,
    mutationFn: (props: { email: string }) =>
      sendRequest<AlgoInput>(Queries.AlgoInputs.endpoint, {
        pathParams: { email: props.email },
      }),
  });

export const useRunAlgo = () =>
  useMutation({
    mutationKey: Queries.RunAlgo.key,
    mutationFn: (body: any) =>
      sendRequest<AlgoOutput>(Queries.RunAlgo.endpoint, {
        body,
      }),
  });

export const useAnalytes = () =>
  useQuery({
    queryKey: Queries.Analytes.key,
    queryFn: () => sendRequest<AnalyteReport[]>(Queries.Analytes.endpoint),
  });

export const useDnaCategoryResults = () =>
  useQuery({
    queryKey: Queries.DnaCategoryResults.key,
    queryFn: () =>
      sendRequest<UserDnaCategory[]>(Queries.DnaCategoryResults.endpoint),
  });

export const useHormones = () =>
  useQuery({
    queryKey: Queries.Hormones.key,
    queryFn: () => sendRequest<HormoneReport[]>(Queries.Hormones.endpoint),
  });

export const useMe = () =>
  useQuery({
    queryKey: Queries.Me.key,
    queryFn: () => sendRequest<UserGetOut>(Queries.Me.endpoint),
  });

export const useNutrients = () =>
  useQuery({
    queryKey: Queries.Nutrients.key,
    queryFn: () =>
      sendRequest<UserNutrientSummary[]>(Queries.Nutrients.endpoint),
  });

export const useQuiz = (code: string) =>
  useMutation({
    mutationKey: [Queries.Quiz.key, code],
    mutationFn: () =>
      sendRequest<Quiz>(Queries.Quiz.endpoint, { pathParams: { code } }),
  });

export const useSubmitQuizResponse = () =>
  useMutation({
    mutationKey: [Queries.SubmitQuizResponse.key],
    mutationFn: (quiz: QuizResponse) =>
      sendRequest(Queries.SubmitQuizResponse.endpoint, { body: quiz }),
  });

export const useUserRequest = () =>
  useMutation({
    mutationKey: [Queries.UserRequest.key],
    mutationFn: (props: { userId: string; requestType: string }) =>
      sendRequest<UserGetOut>(Queries.UserRequest.endpoint, {
        pathParams: { id: props.userId },
        body: { request_type: props.requestType },
      }),
    onSettled: () => {
      // When a user requests adding/removing omega-3, the backend changes the
      // user's omega-3 status. Invalidate the user query so we refetch and the
      // new status is reflected in the UI.
      queryClient.invalidateQueries(Queries.Me.key);
    },
  });

export const useSanitySettings = () => {
  return useQuery(['sanitySettings'], fetchSanitySettings);
};
